import React, { useMemo, useRef, useState } from 'react';
import {
  AlertCircle, AlarmClock, Clock, Crosshair, Eye, Filter, Layers, Tags, TrendingDown, TriangleAlert, UserCog, Users,
} from 'lucide-react';
import { SkeletonStatCards, SkeletonTable } from '../../../components/common/SkeletonLoader';
import {
  formatCents,
  formatIsoDate,
  usePipelineReport,
  type StageFigures,
  type AdviserOption,
  type LossReasonFigures,
  type AttentionRow,
  type WindowSelection,
  type FilterOption,
} from '../../../hooks/pipelineTracker/usePipelineReport';
import {
  SURFACE, Tile, Panel, Pill, FunnelRow, AgeBucket, Insight, Honesty, ComingSoonList,
  heatStyle, stageColour, money, num, type ComingSoonItem,
} from './pipelineParts';
import StageMappingPanel from './StageMappingPanel';
import StageStrip from './StageStrip';
import PeriodBand, {
  AsAtChip, COMPLIANCE_SNAPSHOT_WAITING, PeriodChip, TrailingChip, UNDATED_STAGES_ITEM, currentWindow, periodPresets,
} from './PeriodBand';
import CohortBand, { resolveCohortWindow } from './CohortBand';
import PeopleTable from './PeopleTable';
import BrokerRosterPanel from './BrokerRosterPanel';
import ProcessingBand from './ProcessingBand';
import ConversionSection from './ConversionSection';
import { SectionJumpNav, TrackerSection, sectionAccent } from './TrackerSection';
import { Chip, HousePanel, HouseStatCard, HouseStatGrid, PeriodBar, chartColour, hexRgb } from './trackerKit';
import { EARLIEST_WINDOW_START, canEditTaxonomy } from '../../../hooks/pipelineTracker/reportContract';
import { usePipelineTargets } from '../../../hooks/pipelineTracker/usePipelineTargets';
import { hasAnyTarget, resolveTargetScope } from '../../../hooks/pipelineTracker/targetsContract';
import MovementTrendSection from './MovementTrendSection';
import TargetsEditor, { partnerOptionsFromRows } from './TargetsEditor';
import { useLeadSourceReport } from '../../../hooks/pipelineTracker/useLeadSourceReport';
import { useSalesAnalyticsReport } from '../../../hooks/pipelineTracker/useSalesAnalyticsReport';
import { DEFAULT_MIX_STAGE, type MixStage } from '../../../hooks/pipelineTracker/salesAnalyticsTypes';
import {
  DealMixSection, ForecastPanel, PipelineVelocityPanel, PlannedSettlementsPanel,
  SalesAnalyticsState, VelocityTrendPanel, waitingWheelItems,
} from './SalesAnalyticsSections';

/**
 * Deals in flight, by canonical stage, with exact age in stage.
 *
 * EVERY FIGURE IS COMPUTED FROM THE COMMITTED SNAPSHOT. There is no sample
 * data in this file. The report is fenced to the upload id named by the
 * publication pointer, so a half-written upload is invisible here by
 * construction.
 *
 * STAGE-TO-STAGE CONVERSION IS THE ONE SECTION THAT IS NOT FROM ONE SNAPSHOT.
 * The export carries a deal's CURRENT stage only, so a conversion rate drawn
 * from one snapshot would be an estimate dressed as a measurement. The
 * conversion section instead follows each deal by its reference across every
 * weekly upload, and shows no rate until four uploads exist (ConversionSection).
 */

export interface PipelineTabProps {
  companyId: string;
  asAtDate?: string;
  /**
   * Caller's role, used ONLY to decide whether the mapping controls render.
   * The callable applies the real gate; this keeps a viewer who cannot edit
   * from being shown a control that would be refused.
   */
  role?: string;
}

const BUCKETS = [
  { key: '0-7' as const, label: '0 to 7 days', colour: '#16a34a', field: 'age0to7' as const },
  { key: '8-30' as const, label: '8 to 30 days', colour: '#2563eb', field: 'age8to30' as const },
  { key: '31-90' as const, label: '31 to 90 days', colour: '#b45309', field: 'age31to90' as const },
  { key: '90+' as const, label: 'over 90 days', colour: '#b91c1c', field: 'age90plus' as const },
];

const sum = (stages: readonly StageFigures[], field: keyof StageFigures): number =>
  stages.reduce((n, s) => n + (Number(s[field]) || 0), 0);

/**
 * One loss bar, in a colour of its own (founder, 5 Oct 2026: colour like the panels above): a stage
 * row takes that stage's funnel colour, a reason row a palette hue, each as a left-to-right gradient.
 */
const LossRow: React.FC<{ row: LossReasonFigures; max: number; colour: string }> = ({ row, max, colour }) => (
  <div data-testid="loss-row">
    <div className="flex items-baseline justify-between text-[0.8125rem]">
      <span className="flex items-center gap-2 text-gray-200">
        <i aria-hidden="true" className="inline-block h-2 w-2 shrink-0 rounded-sm" style={{ background: colour }} />
        {row.category}
      </span>
      <b className="tabular-nums text-gray-100">{num(row.dealCount)}</b>
    </div>
    <div aria-hidden="true" className="mt-1 h-2 overflow-hidden rounded-full bg-gray-900/70">
      <div
        className="h-full rounded-full"
        data-colour={colour}
        style={{
          width: `${Math.max(2, (row.dealCount / max) * 100)}%`,
          background: `linear-gradient(90deg, rgba(${hexRgb(colour)},0.55), ${colour})`,
          boxShadow: `0 0 0.5rem rgba(${hexRgb(colour)},0.35)`,
        }}
      />
    </div>
  </div>
);

/**
 * Each group's accent: the one source for its step label, its pill on the jump bar and, through the
 * TrackerSection, every panel inside it. Step order round the palette, so neighbours never match and
 * only "Needs attention" is amber.
 */
const ACCENT = {
  today: sectionAccent(0),
  attn: sectionAccent(1),
  period: sectionAccent(2),
  flow: sectionAccent(3),
  next: sectionAccent(4),
  brokers: sectionAccent(5),
  losses: sectionAccent(6),
  data: sectionAccent(7),
};

/** The window the tab opens on, and the one "Clear dates" returns to. */
export const DEFAULT_PERIOD: WindowSelection = 'fytd';

/**
 * The tab's eight groups, in reading order (founder-approved mockup, 5 Oct 2026): the book as it
 * stands, what needs working, what happened in the period, how deals flow, what is coming, who
 * holds what, what was lost, and what sits outside the figures. A regroup of the panels that were
 * already here: no panel is restyled, and every figure, note and control is kept.
 */
export const SECTIONS: ReadonlyArray<{ id: string; label: string; accent: string }> = [
  { id: 'p-today', label: '1 Pipeline today', accent: ACCENT.today },
  { id: 'p-attn', label: '2 Needs attention', accent: ACCENT.attn },
  { id: 'p-period', label: '3 Period performance', accent: ACCENT.period },
  { id: 'p-flow', label: '4 Flow and conversion', accent: ACCENT.flow },
  { id: 'p-next', label: '5 Next four months', accent: ACCENT.next },
  { id: 'p-brokers', label: '6 Brokers', accent: ACCENT.brokers },
  { id: 'p-losses', label: '7 Losses', accent: ACCENT.losses },
  { id: 'p-data', label: '8 Data quality', accent: ACCENT.data },
];

export const NO_TARGETS_PROMPT = 'No targets set yet. Add them under Targets and monthly inputs.';

const PipelineTab: React.FC<PipelineTabProps> = ({ companyId, asAtDate, role }) => {
  const [adviserId, setAdviserId] = useState<string | undefined>(undefined);
  // FYTD by default (founder, 1 Oct 2026): early in a month MTD holds a few days of events and the
  // first screen read as empty, while the financial year to date always has the practice's figures.
  // All five windows arrive in one response, so switching to MTD costs no round trip.
  const [period, setPeriod] = useState<WindowSelection>(DEFAULT_PERIOD);
  // The APPLIED custom window, not the one being typed: the draft lives in the band, and only
  // a press of Apply moves it here, where it becomes a refetch.
  const [customWindow, setCustomWindow] = useState<{ from: string; to: string } | null>(null);
  // Empty string means "all", which is a real choice rather than an absent one.
  const [workflowName, setWorkflowName] = useState('');
  const [dealLabel, setDealLabel] = useState('');
  // Scoping to an acquired book. A person always beats the group holding them, which the server
  // also enforces, so the two can never combine into an empty page.
  const [groupKey, setGroupKey] = useState<string | undefined>(undefined);
  const { response, loading, error, refetch } = usePipelineReport({
    companyId,
    asAtDate,
    adviserId,
    ...(groupKey !== undefined && adviserId === undefined ? { groupKey } : {}),
    ...(workflowName !== '' ? { workflowName } : {}),
    ...(dealLabel !== '' ? { dealLabel } : {}),
    ...(customWindow !== null
      ? { windowStart: customWindow.from, windowEnd: customWindow.to }
      : {}),
  });
  const report = response !== null && response.status === 'ok' ? response : null;
  // Phase 3 sections: same scope, same window. A custom window only once it is applied, and only
  // while Custom is the selection, so these sections never show dates the bands above do not.
  const customApplied = period === 'custom' && customWindow !== null;
  // Custom chosen, no dates applied yet. Deal movements then shows its fallback window (the
  // first rolling period) under that window's own label; the sections below follow it through the
  // band's own resolver, so they cannot keep the previous period's figures or show other dates.
  const customPending = period === 'custom' && !customApplied;
  const shownWindow =
    // `periods` is absent on a payload from before the period band existed; the band then renders
    // nothing, and so does this.
    report !== null && Array.isArray(report.periods) && report.periods.length > 0
      ? currentWindow(report.periods, period, customApplied ? report.customPeriod : undefined)
      : null;
  const pendingFallback =
    customPending && shownWindow !== null && shownWindow.period !== 'custom' ? shownWindow : null;
  const salesScope = {
    companyId,
    asAtDate,
    ...(adviserId !== undefined ? { adviserId } : {}),
    ...(groupKey !== undefined && adviserId === undefined ? { groupKey } : {}),
    ...(workflowName !== '' ? { workflowName } : {}),
    ...(dealLabel !== '' ? { dealLabel } : {}),
    ...(customApplied && customWindow !== null
      ? { windowStart: customWindow.from, windowEnd: customWindow.to }
      : period !== 'custom'
        ? { period }
        : pendingFallback !== null && pendingFallback.period !== 'custom'
          ? { period: pendingFallback.period }
          : {}),
    enabled: period !== 'custom' || customApplied || pendingFallback !== null,
  };
  const sales = useSalesAnalyticsReport(salesScope);
  // Deal mix's stage choice (Matt Spears' Sales mock up). Settlements reads the call above, so the
  // default tab makes exactly the request it made before the choice existed. Another stage is a
  // second call with the same scope and window; only Deal mix reads it, so changing the stage
  // never blanks Velocity, Forecast or Planned while it loads.
  const [mixStage, setMixStage] = useState<MixStage>(DEFAULT_MIX_STAGE);
  const stageSales = useSalesAnalyticsReport({
    ...salesScope,
    mixStage,
    enabled: salesScope.enabled && mixStage !== DEFAULT_MIX_STAGE,
  });
  const mixCall = mixStage === DEFAULT_MIX_STAGE ? sales : stageSales;
  const mixData = mixCall.response !== null && mixCall.response.status === 'ok' ? mixCall.response : null;
  // Stated above each windowed block while Custom waits for dates, in the band's own words.
  const pendingNote =
    pendingFallback !== null ? (
      <p className="text-xs text-amber-300" role="status">
        Custom dates are not applied yet, so, like Deal movements, this shows {pendingFallback.label}:{' '}
        {formatIsoDate(pendingFallback.window.from)} to {formatIsoDate(pendingFallback.window.to)}.
      </p>
    ) : null;
  const salesData = sales.response !== null && sales.response.status === 'ok' ? sales.response : null;

  // Targets (V6 phase 2). Loaded once for the tab, so the movement tiles, the trend and the editor
  // read one copy and a save recolours the tiles at once. Resolved for the scope on screen: a
  // broker is graded only on their own targets, and a book or a deal-type / label filter on none.
  // Undefined until loaded, so a tile never flashes a target line before the answer arrives.
  const targetsState = usePipelineTargets(companyId || undefined);
  const targetScope = useMemo(
    () =>
      targetsState.targets === null
        ? undefined
        : resolveTargetScope(targetsState.targets, {
            adviserId,
            groupKey: adviserId === undefined ? groupKey : undefined,
            filtered: workflowName !== '' || dealLabel !== '',
          }),
    [targetsState.targets, adviserId, groupKey, workflowName, dealLabel],
  );
  // No target anywhere: not for the practice, not for any broker. The tiles then carry no target
  // line at all, and administrators get one prompt under Deal movements instead of "No target set"
  // on every tile (founder, 1 Oct 2026).
  const noTargetsAtAll =
    targetsState.targets !== null &&
    !hasAnyTarget(targetsState.targets.stages) &&
    !Object.values(targetsState.targets.byBroker ?? {}).some((own) => hasAnyTarget(own));
  // The editor is a disclosure; the prompt opens it and brings it into view.
  const [targetsOpen, setTargetsOpen] = useState(false);
  // Referrer targets are keyed by the Leads & Partners partner key, so the editor offers that report's
  // resolved partners and nothing free-typed. The report is read on the whole export, and only once
  // the editor is opened, so a tab that never opens it never pays for the read.
  const leadsForPartners = useLeadSourceReport(targetsOpen ? companyId || undefined : undefined);
  const partnerOptions = useMemo(
    () => partnerOptionsFromRows(leadsForPartners.result?.status === 'ok' ? leadsForPartners.result.data.partnerRows : undefined),
    [leadsForPartners.result]
  );
  const targetsEditorRef = useRef<HTMLDetailsElement>(null);

  const funnelMax = useMemo(
    () => Math.max(1, ...(report?.stages ?? []).map((st: StageFigures) => st.dealCount)),
    [report],
  );

  // Watch items are DERIVED from this snapshot, never a fixed list: the stages
  // that actually carry ageing deals, worst first. A tenant with a clean book
  // sees an empty worklist rather than invented work.
  //
  // Computed BEFORE the early returns. A hook after a conditional return
  // changes hook order between renders, which React treats as a different
  // component and which no unit test here exercises.
  const watchItems = useMemo(
    () => (report?.stages ?? [])
      .flatMap((st: StageFigures) => [
        { label: `${st.label} past 90 days`, count: st.age90plus },
        { label: `${st.label} past 30 days`, count: st.age31to90 },
      ])
      .filter((w: { count: number }) => w.count > 0)
      .sort((a: { count: number }, b: { count: number }) => b.count - a.count)
      .slice(0, 6),
    [report],
  );

  if (!companyId) {
    return (
      <div className={`${SURFACE} p-8 text-center text-sm text-gray-400`}>
        No company is selected, so there is nothing to report on.
      </div>
    );
  }

  if (loading && !report) {
    return (
      <div className="space-y-6">
        <SkeletonStatCards count={4} columns={4} />
        <SkeletonTable rows={9} columns={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
        <p>{error}</p>
        {/* A failed load must never render as an empty pipeline: a tenant cannot
            tell "nothing to show" from "we could not fetch it". */}
        <button
          type="button"
          onClick={refetch}
          className="mt-3 rounded-md border border-amber-500/40 px-3 py-1.5 text-xs font-semibold text-amber-100 hover:bg-amber-500/10"
        >
          Try again
        </button>
      </div>
    );
  }

  // BEFORE the no-snapshot card, because a refused window is not a missing snapshot and saying
  // so would send the operator to the Upload tab to fix a date they typed. The dates that
  // caused it are still held in state, so the way out is offered here rather than left to a
  // page reload: the band that owns the control is not rendered on this branch.
  if (response !== null && response.status === 'invalid_request') {
    return (
      <div className="rounded-lg border border-amber-900/60 bg-amber-950/30 p-4 text-sm text-amber-300">
        <p>That reporting window could not be used: {response.reason}</p>
        <button
          type="button"
          onClick={() => {
            setCustomWindow(null);
            setPeriod(DEFAULT_PERIOD);
          }}
          className="mt-3 rounded-md border border-amber-500/40 px-3 py-1.5 text-xs font-semibold text-amber-100 hover:bg-amber-500/10"
        >
          Clear dates
        </button>
      </div>
    );
  }

  if (!report) {
    return (
      <div className={`${SURFACE} p-8 text-center`}>
        <p className="text-gray-200">No pipeline snapshot has been uploaded yet</p>
        <p className="mx-auto mt-1 max-w-xl text-sm text-gray-400">
          Upload a Performance export from the Upload tab. Every figure on this page is computed
          from that single file and needs no history.
        </p>
      </div>
    );
  }

  const { totals, stages, exceptions, advisers, lossReasons, attention, terminal } = report;
  const workflowOptions = report.workflows ?? [];
  const labelOptions = report.labels ?? [];
  // `periods` is absent on a payload from before the period band existed.
  const hasPeriods = report.periods !== undefined && report.periods.length > 0;
  const inScope = totals.inFlightDeals;

  // Split by what the label actually claims. A stored category or a lost-class stage is a WHY;
  // a deal lost while still in an active stage tells us only WHERE it stopped.
  const lossWhy = lossReasons.filter(
    (r: LossReasonFigures) => r.basis === 'category' || r.basis === 'lost_stage'
  );
  const lossWhere = lossReasons.filter(
    (r: LossReasonFigures) => r.basis === 'active_stage_at_loss' || r.basis === 'not_recorded'
  );
  const totalLosses = lossReasons.reduce((n: number, r: LossReasonFigures) => n + r.dealCount, 0);
  const whyCount = lossWhy.reduce((n: number, r: LossReasonFigures) => n + r.dealCount, 0);
  const topLoss = lossWhy[0];
  const lossMax = Math.max(1, ...lossReasons.map((r: LossReasonFigures) => r.dealCount));
  // A stage row wears the colour that stage has in the funnel above; "not recorded" is grey.
  const lossStageColour = (r: LossReasonFigures, i: number): string => {
    if (r.basis === 'not_recorded') return '#6b7280';
    const at = stages.findIndex((st: StageFigures) => st.label === r.category);
    return at >= 0 ? stageColour(at) : chartColour(i + lossWhy.length);
  };

  // The window the two bands above are showing, resolved BY the same function CohortBand uses
  // rather than merely in the same way, so the per-broker spread in the people table cannot
  // drift from the band it sits under. A custom window that has been chosen but not applied has
  // no figures yet: the resolver returns undefined and the table falls back to its four original
  // columns rather than showing five empty ones. The undefined check ahead of the call is the
  // band's OWN render condition, not merely a similar one: the spread is read as a breakdown of
  // the band above it, so five columns with no band on the page would be five numbers a reader
  // has nothing to reconcile against.
  // Every section on this tab that cannot be shown yet, listed once at the foot of the tab rather
  // than drawn as cards: the three waiting compliance metrics, the three undated stages
  // of Deal movements, and any Deal mix wheel whose Sales export is missing or whose field is
  // recorded on none of the window's settled deals.
  const comingSoon: ComingSoonItem[] = [
    ...COMPLIANCE_SNAPSHOT_WAITING,
    UNDATED_STAGES_ITEM,
    ...(salesData !== null ? waitingWheelItems(salesData.dealMix) : []),
  ];

  const selectedCohort =
    report.cohorts === undefined
      ? undefined
      : resolveCohortWindow(report.cohorts, period, report.customCohort);

  // Coverage as a percentage, because "75% resolve" is a health reading a
  // reader can act on where a bare pair of counts is not.
  const coverageTotal = report.stageCoverage.mappedDeals + exceptions.unmappedStageDeals;
  const stageCoveragePct = coverageTotal === 0
    ? 100
    : Math.round((report.stageCoverage.mappedDeals / coverageTotal) * 100);

  // Scope chips (founder, 5 Oct 2026: "data isn't changing from WTD to FYTD", read off Pipeline
  // today, which is a photograph by design). Every group and panel says what drives it: the period
  // chosen above, or the snapshot date.
  const asAtChip = <AsAtChip asAtDate={report.provenance.asAtDate} />;
  const periodChip =
    shownWindow !== null ? (
      <PeriodChip period={shownWindow.period} from={shownWindow.window.from} to={shownWindow.window.to} />
    ) : null;
  const velocityChip =
    salesData === null
      ? null
      : salesData.velocity.basis === 'trailing_year'
        ? <TrailingChip>12 months to {formatIsoDate(salesData.velocity.to)}</TrailingChip>
        : periodChip;

  return (
    <div className="space-y-4">
      {/* Broker scope, ABOVE the period band.

          Matt Spears, 8 Sep 2026: "Change Advisers to Brokers and move this
          above the first dashboard". Scope is the first decision a reader makes,
          so it has to be visible before any figure is: a broker chip selected
          below the numbers means the numbers were read before the scope was
          known.

          Chips, not a dropdown: a practice has a handful of brokers and a select
          hides them behind a click. */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-xs text-gray-400">Broker</span>
        <button
          type="button"
          onClick={() => setAdviserId(undefined)}
          className={`rounded-full border px-3 py-1.5 text-[0.8125rem] transition-colors ${
            adviserId === undefined
              ? 'border-blue-600 bg-blue-600 font-semibold text-white'
              : 'border-gray-700 text-gray-300 hover:bg-white/[0.06] hover:text-gray-100'
          }`}
        >
          All brokers
        </button>
        {advisers.map((a: AdviserOption) => (
          <button
            key={a.adviserId ?? 'unassigned'}
            type="button"
            onClick={() => setAdviserId(a.adviserId ?? undefined)}
            className={`rounded-full border px-3 py-1.5 text-[0.8125rem] transition-colors ${
              adviserId === a.adviserId
                ? 'border-blue-600 bg-blue-600 font-semibold text-white'
                : 'border-gray-700 text-gray-300 hover:bg-white/[0.06] hover:text-gray-100'
            }`}
          >
            {a.adviserName}
          </button>
        ))}
        <Pill>{num(inScope)} deals in scope</Pill>
        {loading && <span className="text-xs text-gray-500">Updating…</span>}
      </div>

      {/*
        Period and the deal type / label filters, on one row on a wide screen (founder, 5 Oct 2026),
        wrapping on a narrow one. With the broker chips above, these are the choices a reader makes
        before reading any figure (Evoke, 25 Sep 2026).

        Period sets the WINDOWED figures only: deal movements, the cohort band and the broker
        columns. No period can narrow "deals in flight", so the line under the row says where its
        reach stops rather than leaving a selector above unfiltered figures to be misread.

        Deal type and label are dropdowns rather than chips because a practice runs a handful of
        workflows but can run dozens of labels, and a wall of label chips is the adviser-chip problem
        again. Counts are shown so the operator can tell a filter worth using from one that will
        empty the page. The lists come back UNFILTERED, so choosing one never removes the others and
        a filter can always be cleared. A dropdown that empties itself once you use it is a trap.
      */}
      {(hasPeriods || workflowOptions.length > 0 || labelOptions.length > 0) && (
        <div className="space-y-1.5" data-testid="period-filter-row">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            {hasPeriods && (
              <div className="flex flex-wrap items-center gap-2" data-testid="period-group">
                <PeriodBar
                  presets={periodPresets(report.periods ?? [])}
                  selected={period}
                  onSelect={(key) => setPeriod(key as WindowSelection)}
                  custom={report.customPeriod}
                  onApplyCustom={(from, to) => setCustomWindow({ from, to })}
                  minDate={EARLIEST_WINDOW_START}
                  maxDate={report.periods?.[0]?.to}
                />
              </div>
            )}

            {(workflowOptions.length > 0 || labelOptions.length > 0) && (
              <div className="flex flex-wrap items-center gap-3" data-testid="filter-group">
                <span className="mr-1 text-xs text-gray-400">Filter</span>
                {workflowOptions.length > 0 && (
                  <label className="flex items-center gap-2 text-xs text-gray-400">
                    Deal type
                    <select
                      value={workflowName}
                      onChange={(e) => setWorkflowName(e.target.value)}
                      className="rounded-md border border-gray-600 bg-gray-900 px-2.5 py-1.5 text-[0.8125rem] text-gray-200"
                    >
                      <option value="">All deal types</option>
                      {workflowOptions.map((o: FilterOption) => (
                        <option key={o.name} value={o.name}>{o.name} ({o.count})</option>
                      ))}
                    </select>
                  </label>
                )}

                {labelOptions.length > 0 && (
                  <label className="flex items-center gap-2 text-xs text-gray-400">
                    Label
                    <select
                      value={dealLabel}
                      onChange={(e) => setDealLabel(e.target.value)}
                      className="rounded-md border border-gray-600 bg-gray-900 px-2.5 py-1.5 text-[0.8125rem] text-gray-200"
                    >
                      <option value="">All labels</option>
                      {labelOptions.map((o: FilterOption) => (
                        <option key={o.name} value={o.name}>{o.name} ({o.count})</option>
                      ))}
                    </select>
                  </label>
                )}

                {(workflowName !== '' || dealLabel !== '') && (
                  <button
                    type="button"
                    onClick={() => { setWorkflowName(''); setDealLabel(''); }}
                    className="rounded-md border border-gray-700 px-2.5 py-1.5 text-xs text-gray-300 hover:bg-white/[0.06]"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>
          {hasPeriods && (
            <p className="text-xs text-gray-500" data-testid="period-reach">
              Sets deal movements, the cohort band and broker columns. Everything else is as at{' '}
              {formatIsoDate(report.provenance.asAtDate)}.
            </p>
          )}
        </div>
      )}

      <SectionJumpNav items={SECTIONS} />

      <div className="space-y-10">
        <TrackerSection
          id="p-today"
          accent={ACCENT.today}
          step="1 · Pipeline today"
          title="Where the book stands"
          description="Age is days in the current stage at the snapshot date, not today."
          actions={asAtChip}
        >
          {/* The rule, stated before the four tiles and the stage figures. The broker table carries In
              flight too (Evoke, 25 Sep 2026): the line beside Period says those are as at the
              snapshot, and this banner states what "in flight" means. */}
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
            <p className="text-sm text-blue-100">
              A deal is in flight only when its stage maps to an active stage AND it carries no settled
              date and no not-proceeding date. A deal that has left the funnel is shown separately and is
              in none of the stage figures.
            </p>
            <p className="mt-1 text-xs text-blue-200/70">
              As at {formatIsoDate(report.provenance.asAtDate)}. Age is days in the current stage as at the snapshot
              date, not as at today.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Tile
              label="Deals in flight"
              value={num(totals.inFlightDeals)}
              footer="no settled date and not marked as not proceeding"
              tone="brand"
            />
            <Tile
              label="Pipeline value"
              value={money(totals.inFlightValueCents)}
              footer={`${formatCents(totals.inFlightValueCents)} across deals in flight`}
              tone="green"
            />
            <Tile
              label="Stalled over 30 days"
              value={num(totals.over30Deals)}
              footer={`${num(totals.over90Deals)} of those sit past 90 days`}
              tone="amber"
            />
            <Tile
              label="Median days in stage"
              value={totals.medianAgeDays === null ? '-' : num(totals.medianAgeDays)}
              footer="measured from the date each deal entered its current stage"
              tone="violet"
            />
          </div>

          {/*
            Where the money is stuck, before what shape the book is.

            The funnel below answers "what shape is my pipeline". This answers
            "which stage is holding my deals", which is the question a principal
            opens the tab for, so it comes first. Same figures, same snapshot: the
            strip carries no number the funnel does not.
          */}
          <StageStrip
            accent={ACCENT.today}
            stages={stages}
            totalActiveDeals={totals.inFlightDeals}
            postSettlement={{ count: terminal.settledCount, valueCents: terminal.settledValueCents }}
          />

          <Panel
            title="Canonical stage funnel"
            icon={Filter}
            accent={ACCENT.today}
            badge={
              <Pill>
                Post-settlement: {num(terminal.settledCount)} deals, {money(terminal.settledValueCents)}
              </Pill>
            }
            blurb="Your Salestrekker stage names mapped onto one canonical ladder, so the funnel reads the same every week even when a workflow is renamed. Post-settlement stages sit outside the funnel."
          >
            {stages.length === 0 ? (
              <p className="text-sm text-gray-400">No deals resolve to a canonical stage in this snapshot.</p>
            ) : (
              <div className="flex flex-col gap-3.5">
                {stages.map((st: StageFigures, i: number) => (
                  <FunnelRow
                    key={st.canonicalKey}
                    label={st.label}
                    medianAgeDays={st.medianAgeDays}
                    deals={st.dealCount}
                    valueCents={st.totalValueCents}
                    widthPct={(st.dealCount / funnelMax) * 100}
                    colour={stageColour(i)}
                  />
                ))}
              </div>
            )}
          </Panel>

          <Panel
            title="Age in stage"
            icon={AlarmClock}
            accent={ACCENT.today}
            blurb={`Days since each deal entered its current stage, measured at the snapshot date of ${formatIsoDate(report.provenance.asAtDate)}. Flagged over 30 days, red over 90 days.`}
          >
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {BUCKETS.map((b) => (
                <AgeBucket key={b.key} label={b.label} value={sum(stages, b.field)} colour={b.colour} />
              ))}
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full border-separate border-spacing-y-1 text-sm">
                <thead className="text-[0.6875rem] uppercase tracking-wider text-gray-400">
                  <tr>
                    <th className="px-3 py-1.5 text-left font-semibold">Stage</th>
                    {BUCKETS.map((b) => (
                      <th key={b.key} className="px-3 py-1.5 text-center font-semibold">{b.key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stages.map((st: StageFigures) => (
                    <tr key={st.canonicalKey}>
                      <td className="px-3 py-2 text-gray-300">{st.label}</td>
                      {BUCKETS.map((b) => {
                        const n = Number(st[b.field]) || 0;
                        return (
                          <td
                            key={b.key}
                            className="rounded-md px-3 py-2 text-center font-semibold tabular-nums"
                            style={heatStyle(n, b.key)}
                          >
                            {n || '·'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </TrackerSection>

        <TrackerSection
          id="p-attn"
          accent={ACCENT.attn}
          step="2 · Needs attention"
          title="Work through these first"
          description="Worklists from your stage names, age in stage and the estimated settlement dates brokers keep."
          actions={asAtChip}
        >
          <Panel
            title="Watch items this week"
            icon={Eye}
            accent={ACCENT.attn}
            badge={<Pill tone="brand">{adviserId ? advisers.find((a: AdviserOption) => a.adviserId === adviserId)?.adviserName ?? 'Broker' : 'All brokers'}</Pill>}
            blurb="Worklists built from your stage names and age in stage."
          >
            {watchItems.length === 0 ? (
              <p className="text-sm text-gray-400">Nothing ageing past 30 days in this snapshot.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {watchItems.map((w: { label: string; count: number }) => (
                  <div
                    key={w.label}
                    className="flex items-center justify-between gap-4 rounded-lg border border-gray-700/70 bg-gray-900/45 px-3 py-2.5 text-[0.8125rem] text-gray-300"
                  >
                    <span>{w.label}</span>
                    <b className="text-lg font-bold tabular-nums text-amber-400">{num(w.count)}</b>
                  </div>
                ))}
              </div>
            )}
          </Panel>
          {/* Collapsed by default (founder, 5 Oct 2026): a long table that pushed every later group
              down the page. The header, its count and the arrow stay in view, so the worklist is
              one click away and its size is visible without opening it. */}
          <HousePanel
            title="Needs attention"
            icon={TriangleAlert}
            color={ACCENT.attn}
            collapsible
            defaultOpen={false}
            badge={
              <>
                <Pill tone="bad">Longest sitting deals in flight</Pill>
                <span data-testid="needs-attention-count">
                  <Chip tone="amber">{num(attention.length)} {attention.length === 1 ? 'deal' : 'deals'}</Chip>
                </span>
              </>
            }
            blurb="Sorted by days in stage. These are the deals a weekly pipeline meeting should work through first."
          >
            <div>
              {attention.length === 0 ? (
                <p className="text-sm text-gray-400">No deal is past its stage threshold.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="text-[0.6875rem] uppercase tracking-wider text-gray-400">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Deal ref</th>
                        <th className="px-3 py-2 font-semibold">Canonical stage</th>
                        <th className="px-3 py-2 font-semibold">Salestrekker stage</th>
                        <th className="px-3 py-2 font-semibold">Broker</th>
                        <th className="px-3 py-2 text-right font-semibold">Deal value</th>
                        <th className="px-3 py-2 text-right font-semibold">Days in stage</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/50">
                      {attention.map((row: AttentionRow) => (
                        <tr key={row.dealRef} className="text-gray-300">
                          <td className="px-3 py-2.5 font-mono text-xs text-gray-400">{row.dealRef}</td>
                          <td className="px-3 py-2.5">{row.stageLabel}</td>
                          <td className="px-3 py-2.5 text-gray-400">{row.rawStageName ?? '-'}</td>
                          <td className="px-3 py-2.5">{row.adviser}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{money(row.valueCents)}</td>
                          <td className="px-3 py-2.5 text-right">
                            {/* The threshold travels with the age: 451 days means
                                nothing until you know the stage allows 7. */}
                            <span className={`tabular-nums ${row.bucket === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                              {row.ageDays} days
                            </span>
                            <span className="ml-1 text-xs text-gray-500">of {row.staleAfterDays}</span>
                            <span className={`ml-2 inline-block rounded-md px-2 py-0.5 text-xs font-semibold ${
                              row.bucket === 'critical'
                                ? 'bg-red-500/15 text-red-300'
                                : 'bg-amber-500/15 text-amber-300'
                            }`}
                            >
                              {row.bucket === 'critical' ? 'Critical' : 'Stale'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </HousePanel>
        </TrackerSection>

        <TrackerSection
          id="p-period"
          accent={ACCENT.period}
          step="3 · Period performance"
          title="What happened in the period"
          description={`Sets deal movements, the cohort band, deal mix and broker columns.${shownWindow !== null ? ` Recorded dates in the export, ${formatIsoDate(shownWindow.window.from)} to ${formatIsoDate(shownWindow.window.to)}.` : ''}`}
          actions={periodChip}
        >
          {report.periods !== undefined && (
            <PeriodBand
              accent={ACCENT.period}
              periods={report.periods}
              selected={period}
              custom={report.customPeriod}
              asAtDate={report.provenance.asAtDate}
              targets={noTargetsAtAll ? undefined : targetScope}
              targetsNote={
                noTargetsAtAll && canEditTaxonomy(role) ? (
                  <p className="mt-3 text-xs text-gray-400" data-testid="no-targets-prompt">
                    {NO_TARGETS_PROMPT}{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setTargetsOpen(true);
                        targetsEditorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
                      }}
                      className="font-semibold text-blue-300 hover:text-blue-200"
                    >
                      Set targets
                    </button>
                  </p>
                ) : undefined
              }
            />
          )}

          {/* Same scope as the report above, so a broker chip narrows the line with every other figure. */}
          <MovementTrendSection
            accent={ACCENT.period}
            companyId={companyId}
            asAtDate={report.provenance.asAtDate}
            adviserId={adviserId}
            {...(groupKey !== undefined && adviserId === undefined ? { groupKey } : {})}
            {...(workflowName !== '' ? { workflowName } : {})}
            {...(dealLabel !== '' ? { dealLabel } : {})}
            targets={targetScope}
          />

          {/* Same window as the band above, asked the other way round: not what
              happened between two dates, but what became of what started. */}
          {report.cohorts !== undefined && (
            <CohortBand accent={ACCENT.period} cohorts={report.cohorts} selected={period} custom={report.customCohort} />
          )}

          {pendingNote}
          {/* Another stage keeps its panel (and the stage choice) on screen while it loads; the
              default stage renders exactly as before the choice existed. */}
          {mixData !== null || mixStage !== DEFAULT_MIX_STAGE ? (
            <DealMixSection
              accent={ACCENT.period}
              chip={periodChip}
              mix={mixData?.dealMix ?? null}
              salesAsAtDate={mixData?.sales.asAtDate ?? null}
              stage={mixStage}
              onStageChange={setMixStage}
              pending={
                <SalesAnalyticsState
                  title="Deal mix"
                  response={mixCall.response}
                  loading={mixCall.loading}
                  error={mixCall.error}
                  waitingForDates={customPending && pendingFallback === null}
                  onRetry={mixCall.refetch}
                />
              }
            />
          ) : (
            <SalesAnalyticsState
              title="Deal mix at settlement"
              response={sales.response}
              loading={sales.loading}
              error={sales.error}
              waitingForDates={customPending && pendingFallback === null}
              onRetry={sales.refetch}
            />
          )}

          {/* Under the trend, administrators only: a target is set looking at what the practice
              actually does, which is the chart directly above. */}
          {canEditTaxonomy(role) && (
            <details
              ref={targetsEditorRef}
              open={targetsOpen}
              onToggle={(e) => setTargetsOpen(e.currentTarget.open)}
              data-testid="targets-editor"
              className="group relative scroll-mt-4 overflow-hidden rounded-xl border bg-gray-800/40 px-5 py-3.5"
              style={{
                borderColor: `rgba(${hexRgb(ACCENT.period)},0.3)`,
                background: `linear-gradient(180deg, rgba(${hexRgb(ACCENT.period)},0.08), rgba(31,41,55,0.4) 4rem)`,
              }}
            >
              <span aria-hidden="true" data-panel-accent={ACCENT.period} className="absolute inset-x-0 top-0 h-[0.1875rem]" style={{ background: ACCENT.period }} />
              <summary className="cursor-pointer list-none text-[0.8125rem] text-gray-400 hover:text-gray-200">
                <span className="inline-flex items-center gap-1.5">
                  <Crosshair className="h-3.5 w-3.5" />
                  {targetsState.targetsIsSeed ? 'Set targets and monthly inputs' : 'Targets and monthly inputs'}
                </span>
              </summary>
              <div className="mt-4">
                <TargetsEditor
                  state={targetsState}
                  advisers={advisers}
                  asAtDate={report.provenance.asAtDate}
                  partnerOptions={partnerOptions}
                />
              </div>
            </details>
          )}
        </TrackerSection>

        <TrackerSection
          id="p-flow"
          accent={ACCENT.flow}
          step="4 · Flow and conversion"
          title="How deals move through the stages"
          description="Measured on this export and across the weekly uploads."
        >
          {/* How long each step takes. Scoped by the same filters, so a broker chip narrows this with
              everything else, but NOT by the period: it measures the whole snapshot, which the line
              beside Period says. */}
          {report.processing !== undefined && (
            <ProcessingBand accent={ACCENT.flow} chip={asAtChip} processing={report.processing} />
          )}

          {/* How many deals get from each step to the next. Follows the broker chip so a scoped page
              opens the comparison on that broker. */}
          <ConversionSection
            accent={ACCENT.flow}
            chip={<TrailingChip>Every weekly upload</TrailingChip>}
            companyId={companyId}
            scopedAdviserId={adviserId}
          />

          {pendingNote}
          {salesData !== null ? (
            <>
              <PipelineVelocityPanel accent={ACCENT.flow} chip={velocityChip} velocity={salesData.velocity} />
              <VelocityTrendPanel
                accent={ACCENT.flow}
                chip={<TrailingChip>12 months to {formatIsoDate(report.provenance.asAtDate)}</TrailingChip>}
                trend={salesData.velocityTrend}
              />
            </>
          ) : (
            <SalesAnalyticsState
              title="Pipeline velocity and its trend"
              response={sales.response}
              loading={sales.loading}
              error={sales.error}
              waitingForDates={customPending && pendingFallback === null}
              onRetry={sales.refetch}
            />
          )}
        </TrackerSection>

        <TrackerSection
          id="p-next"
          accent={ACCENT.next}
          step="5 · Next four months"
          title="Settlements coming"
          description="Deals already at submission or later, from the snapshot."
          actions={asAtChip}
        >
          {pendingNote}
          {salesData !== null ? (
            <>
              <ForecastPanel accent={ACCENT.next} forecast={salesData.forecast} />
              {/* Estimates needing a date, by broker, renders inside this panel and stays with it. */}
              <PlannedSettlementsPanel accent={ACCENT.next} planned={salesData.planned} />
            </>
          ) : (
            <SalesAnalyticsState
              title="Pipeline forecast and planned settlements"
              response={sales.response}
              loading={sales.loading}
              error={sales.error}
              waitingForDates={customPending && pendingFallback === null}
              onRetry={sales.refetch}
            />
          )}
        </TrackerSection>

        <TrackerSection
          id="p-brokers"
          accent={ACCENT.brokers}
          step="6 · Brokers"
          title="Who is carrying the book"
          description="Deals in flight per broker, busiest first, with what became of the deals each broker started in the selected window. Click a broker to scope every figure on this tab."
          actions={
            <>
              {periodChip}
              <AsAtChip asAtDate={report.provenance.asAtDate} prefix="In flight as at" />
            </>
          }
        >
          {/* Its columns are the cohort band in Period performance, split by the person who owns each
              deal (Evoke, 25 Sep 2026). */}
          <Panel
            title="Active deals by broker"
            icon={Users}
            accent={ACCENT.brokers}
            badge={
              <Pill>
                <Users className="h-3 w-3" />
                {num(report.people?.brokers.length ?? advisers.length)} brokers
              </Pill>
            }
            blurb="Deals in flight per broker, busiest first, with what became of the deals each broker started in the selected window. Click a broker to scope every figure on this tab to them."
          >
            <PeopleTable
              people={report.people}
              advisers={advisers}
              targets={targetsState.targets}
              inScope={inScope}
              cohort={selectedCohort}
              selectedAdviserId={adviserId}
              onSelectAdviser={(next) => {
                setAdviserId(next);
                // Choosing a person clears the group, so the badge above never claims a scope the
                // figures are not actually on.
                if (next !== undefined) setGroupKey(undefined);
              }}
              selectedGroupKey={groupKey}
              onSelectGroup={(next) => {
                setGroupKey(next);
                if (next !== undefined) setAdviserId(undefined);
              }}
              // The cohort band in Period performance already carries the column totals.
              showTotalRow={false}
            />
            {report.people !== undefined && report.people.unclassifiedCount > 0 && !canEditTaxonomy(role) && (
              <p className="mt-3 text-[0.8125rem] text-gray-400">
                {num(report.people.unclassifiedCount)}{' '}
                {report.people.unclassifiedCount === 1 ? 'person has' : 'people have'} not been marked
                as a broker or as support, so they are listed as brokers above. An administrator can
                classify them.
              </p>
            )}

            {/* The editor sits UNDER the table on purpose. Classifying somebody is a judgement made
                from their deal count, and the counts are right here: 1,690 deals is obviously a
                broker, 1 obviously needs a look. A separate settings page would mean deciding
                without the one number that makes it easy. */}
            {canEditTaxonomy(role) && (
              <details className="mt-4 group">
                <summary className="cursor-pointer list-none text-[0.8125rem] text-gray-400 hover:text-gray-200">
                  <span className="inline-flex items-center gap-1.5">
                    <UserCog className="h-3.5 w-3.5" />
                    {report.people?.isSeed
                      ? 'Set up who counts as a broker'
                      : report.people !== undefined && report.people.unclassifiedCount > 0
                        ? `Edit brokers and books (${num(report.people.unclassifiedCount)} not classified)`
                        : 'Edit brokers and books'}
                  </span>
                </summary>
                <div className="mt-3">
                  <BrokerRosterPanel
                    companyId={companyId}
                    canEdit={canEditTaxonomy(role)}
                    advisers={advisers}
                    onSaved={refetch}
                  />
                </div>
              </details>
            )}
          </Panel>
        </TrackerSection>

        <TrackerSection
          id="p-losses"
          accent={ACCENT.losses}
          step="7 · Losses"
          title="Where deals stop"
          description={totalLosses === 0 ? 'No lost deals in this snapshot.' : whyCount > 0 ? `${num(whyCount)} of ${num(totalLosses)} losses carry a recorded reason.` : `${num(totalLosses)} losses, shown by the stage each deal was sitting in. Salestrekker does not record why.`}
          actions={asAtChip}
        >
          <Panel
            title={whyCount > 0 ? 'Why deals were lost' : 'Where deals were lost'}
            icon={TrendingDown}
            accent={ACCENT.losses}
            blurb={
              whyCount > 0
                ? `${num(whyCount)} of ${num(totalLosses)} losses carry a recorded reason.`
                : `${num(totalLosses)} losses, shown by the stage each deal was sitting in. Salestrekker does not record why.`
            }
          >
            {lossReasons.length === 0 ? (
              <p className="text-sm text-gray-400">No lost deals in this snapshot.</p>
            ) : (
              <>
                {lossWhy.length > 0 && (
                  <div className="space-y-2.5">
                    {lossWhy.map((r: LossReasonFigures, i: number) => (
                      <LossRow key={`why-${r.category}`} row={r} max={lossMax} colour={chartColour(i)} />
                    ))}
                  </div>
                )}

                {lossWhere.length > 0 && (
                  <div className={lossWhy.length > 0 ? 'mt-4 border-t border-gray-700/70 pt-3' : ''}>
                    {/* The heading is the whole fix. These rows were previously rendered under
                        "Loss reasons", which told a reader that 412 deals were lost BECAUSE they
                        were leads. They are stages, and the stage is where the deal stopped. */}
                    <div className="mb-2 text-[0.6875rem] uppercase tracking-wider text-gray-400">
                      Stage the deal was in when it stopped
                    </div>
                    <div className="space-y-2.5">
                      {lossWhere.map((r: LossReasonFigures, i: number) => (
                        <LossRow key={`where-${r.category}`} row={r} max={lossMax} colour={lossStageColour(r, i)} />
                      ))}
                    </div>
                  </div>
                )}

                <Insight>
                  {whyCount > 0 ? (
                    <>
                      <b>{topLoss?.category}</b> is {num(topLoss?.dealCount ?? 0)} of{' '}
                      {num(whyCount)} recorded reasons, {money(topLoss?.totalValueCents ?? 0)} of
                      deal value.
                    </>
                  ) : (
                    <>
                      No loss carries a recorded reason. Salestrekker leaves a dead deal on the
                      stage it stopped at and its only reason field is free text, which stays out
                      of VALUTTEN because brokers type client details into it. So this panel can
                      say where deals stop, which is useful, but not why they were lost.
                    </>
                  )}
                </Insight>
              </>
            )}
          </Panel>
        </TrackerSection>

        <TrackerSection
          id="p-data"
          accent={ACCENT.data}
          step="8 · Data quality"
          title="What sits outside the figures"
          actions={asAtChip}
        >
          {exceptions.excludedDeals > 0 && (
            <Panel
              title={`${num(exceptions.excludedDeals)} deals could not be classified`}
              icon={TriangleAlert}
              accent={ACCENT.data}
              badge={<Pill tone="warn">{money(exceptions.excludedValueCents)} outside every figure</Pill>}
              blurb={
                <>
                  {money(exceptions.excludedValueCents)} of deal value sits outside every figure above.
                  Deals whose stage is blank, whose stage has not been mapped, or whose active stage
                  contradicts a settled or not-proceeding date. They are not lost: map the stage, or
                  correct the export, and they rejoin the figures with no reprocessing.
                </>
              }
            >
              <HouseStatGrid count={3}>
                <HouseStatCard label="Stage not mapped" value={num(exceptions.unmappedStageDeals)} icon={Tags} color={ACCENT.data} tone="warn" />
                <HouseStatCard label="Stage missing" value={num(exceptions.missingStageDeals)} icon={AlertCircle} color={ACCENT.data} tone="warn" />
                <HouseStatCard label="Contradictory dates" value={num(exceptions.contradictoryTerminalDeals)} icon={TriangleAlert} color={ACCENT.data} tone="warn" />
              </HouseStatGrid>
              <StageMappingPanel
                companyId={companyId}
                canEdit={canEditTaxonomy(role)}
                unmapped={report.unmappedStageNames}
                coveragePct={stageCoveragePct}
                onMapped={refetch}
              />
            </Panel>
          )}

          <Honesty>
            <Clock className="mr-1.5 inline h-3.5 w-3.5" />
            Every figure on this page is computed from a single export and needs no history, except
            stage-to-stage conversion, which follows each deal by its reference across the weekly
            snapshots. A stage a deal entered and left between two uploads is invisible to it.
          </Honesty>

          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            {/* Settled is not repeated here: the funnel badge carries the post-settlement deals and
                value (founder, 5 Oct 2026). */}
            <span className="flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5" />
              Lost {num(terminal.lostCount)} &middot; {money(terminal.lostValueCents)}
            </span>
            {exceptions.terminalDateOverrides > 0 && (
              <span>Classified by outcome: {num(exceptions.terminalDateOverrides)}</span>
            )}
          </div>

          {exceptions.terminalDateOverrides > 0 && (
            <p className="text-xs text-gray-500">
              For {num(exceptions.terminalDateOverrides)} deals the recorded outcome decided the
              result rather than the stage, because this provider leaves a finished deal on the stage
              it died in. Where the export still carries a not-proceeding or settled date that date is
              used; where it does not, the deal&rsquo;s current status is.
            </p>
          )}
        </TrackerSection>
      </div>

      <ComingSoonList items={comingSoon} />
    </div>
  );
};

export default PipelineTab;
